import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DisplaySignalRService } from '../../services/display-signalr.service';
import { DisplayCacheService } from '../../services/display-cache.service';
import { RenderPayload, ConnectionState } from '../../models/render-payload';
import { DisplayRendererComponent } from '../../components/display-renderer/display-renderer.component';

@Component({
    selector: 'app-display-page',
    standalone: true,
    imports: [CommonModule, DisplayRendererComponent],
    providers: [DisplaySignalRService],
    templateUrl: './display-page.component.html',
    styleUrl: './display-page.component.scss'
})
export class DisplayPageComponent implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    readonly signalR = inject(DisplaySignalRService);
    private readonly cache = inject(DisplayCacheService);
    readonly debugOverlayEnabled = environment.debugOverlayEnabled;

    hideDebugOverlay = true;
    siteId = '';
    displayId = '';

    readonly activePayload = signal<RenderPayload | null>(null);
    readonly isFallback = signal(false);

    private routeSub: Subscription | null = null;
    private payloadSub: Subscription | null = null;
    private stateWatcher: ReturnType<typeof setInterval> | null = null;
    private disconnectTimer: ReturnType<typeof setTimeout> | null = null;

    get connectionState(): ConnectionState {
        return this.signalR.connectionState();
    }

    ngOnInit(): void {
        // Subscribe to live payloads
        this.payloadSub = this.signalR.renderPayload$.subscribe((payload) => {
            this.applyPayload(payload);
        });

        // Poll connection signal every second to manage the fallback timer
        this.stateWatcher = setInterval(() => this.checkConnectionState(), 1000);

        this.routeSub = this.route.paramMap.subscribe((paramMap) => {
            const nextSiteId = paramMap.get('siteId') ?? '';
            const nextDisplayId = paramMap.get('displayId') ?? '';

            if (!nextDisplayId) {
                return;
            }

            const displayChanged = this.displayId !== nextDisplayId;
            this.siteId = nextSiteId;
            this.displayId = nextDisplayId;

            if (displayChanged) {
                this.activateDisplay();
            }
        });
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe();
        this.payloadSub?.unsubscribe();
        if (this.stateWatcher !== null) clearInterval(this.stateWatcher);
        this.clearDisconnectTimer();
        this.signalR.disconnect();
    }

    private activateDisplay(): void {
        this.signalR.disconnect();
        this.clearDisconnectTimer();
        this.isFallback.set(false);
        this.activePayload.set(null);

        // Restore cached payload immediately so screen is not blank during connect.
        const cached = this.cache.restore(this.displayId);
        if (cached) {
            this.applyRestoredPayload(cached);
        }

        const token = this.getDeviceToken();
        this.signalR.connect(this.displayId, token ?? undefined);
    }

    private applyPayload(payload: RenderPayload): void {
        this.cache.store(this.displayId, payload);
        this.applyRestoredPayload(payload);
        this.isFallback.set(false);
        this.clearDisconnectTimer();
    }

    private applyRestoredPayload(payload: RenderPayload): void {
        this.activePayload.set(payload);
    }

    private checkConnectionState(): void {
        if (!this.displayId) {
            return;
        }

        const state = this.signalR.connectionState();
        if (state === 'disconnected' || state === 'reconnecting') {
            if (!this.disconnectTimer) {
                this.startDisconnectTimer();
            }
        } else if (state === 'connected') {
            this.clearDisconnectTimer();
            this.isFallback.set(false);
        }
    }

    private startDisconnectTimer(): void {
        this.disconnectTimer = setTimeout(() => {
            const fallback = this.cache.getFallback();
            if (fallback) {
                this.activePayload.set(fallback);
            }
            this.isFallback.set(true);
        }, environment.fallbackTimeoutMs);
    }

    private clearDisconnectTimer(): void {
        if (this.disconnectTimer !== null) {
            clearTimeout(this.disconnectTimer);
            this.disconnectTimer = null;
        }
    }

    private getDeviceToken(): string | null {
        // 1. Query string: ?token=...
        const params = new URLSearchParams(window.location.search);
        const qsToken = params.get('token');
        if (qsToken) return qsToken;

        // 2. Cookie: device_token=...
        const match = document.cookie.match(/(?:^|;\s*)device_token=([^;]+)/);
        return match ? decodeURIComponent(match[1]) : null;
    }
}
